import React, { useContext, useEffect, useState } from 'react';
import { Button, Grid, Hidden, Snackbar, TextField, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { useLazyQuery, useMutation } from 'react-apollo';
import { Redirect, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Logo from '../../images/pourwall-logo.svg';
import Illustration from '../../images/illustration-login.svg';
import Glassware from '../../images/glassware.svg';
import GoogleLogo from '../../images/google-logo.svg';

import { ArrowForward } from '@material-ui/icons';

import { SessionContext } from '../../contexts/session';
import { GET_USER, LOGIN, OIDC_CANCEL_LINK, OIDC_CONFIRM_LINK, OIDC_LOGIN, OIDC_START_LOGIN } from './queries';
import style from './styles';
import { LanguageDropdown } from '../../components/LanguageDropdown';
import CustomLink from '../../components/CustomLink';
import { trackCTAClick, trackEvent } from '../../util/analytics';

const useStyles = makeStyles(style);

export const Login = ({ history }) => {
  const { t } = useTranslation();
  let params = useParams();
  let authToken = params?.token,
    tokenUserId = params?.tokenUser;

  const classes = useStyles();
  let { user, token, login } = useContext(SessionContext);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [hasSeenError, setHasSeenError] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [oidcLinkToken, setOidcLinkToken] = useState(null);
  const [oidcPassword, setOidcPassword] = useState('');
  const [oidcProfile, setOidcProfile] = useState(null);
  const [oidcCallbackHandled, setOidcCallbackHandled] = useState(false);

  const [doLogin, { loading, error, data }] = useLazyQuery(LOGIN);
  const [getUserQuery, { data: authTokenUserData }] = useLazyQuery(GET_USER);
  const [startOidcLogin, { loading: oidcStartLoading }] = useMutation(OIDC_START_LOGIN);
  const [finishOidcLogin, { data: oidcLoginData, error: oidcLoginError, loading: oidcLoginLoading }] = useMutation(OIDC_LOGIN);
  const [confirmOidcLink, { data: oidcConfirmData, error: oidcConfirmError, loading: oidcConfirmLoading }] = useMutation(OIDC_CONFIRM_LINK);
  const [cancelOidcLink] = useMutation(OIDC_CANCEL_LINK);

  const go = (e) => {
    e.preventDefault();

    setOpen(false);
    setHasSeenError(false);

    if (email && password) {
      trackCTAClick('Login', 'login_page');
      doLogin({ variables: { email, password } });
    }
  };

  if (error && !hasSeenError) {
    setErrorMessage(t('Login.PleaseCheckEmailOrPassword'));
    setOpen(true);
    setHasSeenError(true);
  }

  if (oidcLoginError && !hasSeenError) {
    setErrorMessage('Google login failed. Please try again or use email/password.');
    setOpen(true);
    setHasSeenError(true);
  }

  if (oidcConfirmError && !hasSeenError) {
    setErrorMessage('Password confirmation failed. Please try again.');
    setOpen(true);
    setHasSeenError(true);
  }

  useEffect(() => {
    if (data?.login?.token && data?.login?.user) {
      login(data.login.user, data.login.token);
      history.push('/taps');
    } else if (oidcConfirmData?.oidcConfirmLink?.token && oidcConfirmData?.oidcConfirmLink?.user) {
      login(oidcConfirmData.oidcConfirmLink.user, oidcConfirmData.oidcConfirmLink.token);
      history.push('/taps');
    } else if (oidcLoginData?.oidcLogin?.token && oidcLoginData?.oidcLogin?.user) {
      login(oidcLoginData.oidcLogin.user, oidcLoginData.oidcLogin.token);
      history.push('/taps');
    } else if (authToken && authTokenUserData?.user) {
      login(authTokenUserData.user, authToken);
      history.push('/taps');
    }
  }, [data, history, login, authToken, authTokenUserData, oidcLoginData, oidcConfirmData]);

  useEffect(() => {
    if (oidcLoginData?.oidcLogin?.requiresOnboarding && oidcLoginData?.oidcLogin?.profile) {
      trackEvent('google_signup_start', {
        event_category: 'Authentication',
        event_label: 'Google Signup - New User',
        method: 'google'
      });
      const params = new URLSearchParams();
      if (oidcLoginData.oidcLogin.profile.email) params.set('email', oidcLoginData.oidcLogin.profile.email);
      if (oidcLoginData.oidcLogin.profile.firstName) params.set('firstName', oidcLoginData.oidcLogin.profile.firstName);
      if (oidcLoginData.oidcLogin.profile.lastName) params.set('lastName', oidcLoginData.oidcLogin.profile.lastName);
      if (oidcLoginData.oidcLogin.onboardingToken) params.set('onboardingToken', oidcLoginData.oidcLogin.onboardingToken);
      history.push({ pathname: '/signup', search: params.toString() ? `?${params.toString()}` : '' });
    }
  }, [oidcLoginData, history]);

  useEffect(() => {
    if (oidcLoginData?.oidcLogin?.requiresPasswordConfirm && oidcLoginData?.oidcLogin?.linkToken) {
      setOidcLinkToken(oidcLoginData.oidcLogin.linkToken);
      setOidcProfile(oidcLoginData.oidcLogin.profile || null);
    }
  }, [oidcLoginData]);

  useEffect(() => {
    if (
      oidcLoginData?.oidcLogin &&
      !oidcLoginData.oidcLogin.token &&
      !oidcLoginData.oidcLogin.requiresOnboarding &&
      !oidcLoginData.oidcLogin.requiresPasswordConfirm &&
      !hasSeenError
    ) {
      setErrorMessage('Google login failed. Please try again or use email/password.');
      setOpen(true);
      setHasSeenError(true);
    }
  }, [oidcLoginData, hasSeenError]);

  useEffect(() => {
    if (authToken && tokenUserId) {
      getUserQuery({ variables: { id: tokenUserId }, context: { headers: { authorization: authToken } } });
    }
  }, [authToken, tokenUserId, getUserQuery]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const code = queryParams.get('code');
    const state = queryParams.get('state');
    if (code && state && !oidcCallbackHandled) {
      setOidcCallbackHandled(true);
      finishOidcLogin({ variables: { code, state } });
      queryParams.delete('code');
      queryParams.delete('state');
      history.replace({
        pathname: window.location.pathname,
        search: queryParams.toString() ? `?${queryParams.toString()}` : ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishOidcLogin, oidcCallbackHandled]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOidcStart = async () => {
    setOpen(false);
    setHasSeenError(false);
    setErrorMessage('');
    setOidcLinkToken(null);
    setOidcProfile(null);
    setOidcPassword('');
    try {
      const result = await startOidcLogin();
      const authUrl = result?.data?.oidcStartLogin?.authUrl;
      if (authUrl) {
        window.location.assign(authUrl);
      }
    } catch (e) {
      setErrorMessage(e.message || 'Unable to start login.');
      setOpen(true);
    }
  };

  const handleOidcConfirm = async (e) => {
    e.preventDefault();
    if (!oidcLinkToken || !oidcPassword) return;
    setOpen(false);
    setHasSeenError(false);
    setErrorMessage('');
    try {
      await confirmOidcLink({ variables: { linkToken: oidcLinkToken, password: oidcPassword } });
    } catch (e) {
      setErrorMessage(e.message || 'Unable to confirm link.');
      setOpen(true);
    }
  };

  const handleOidcCancel = async () => {
    if (oidcLinkToken) {
      try {
        await cancelOidcLink({ variables: { linkToken: oidcLinkToken } });
      } catch (e) {
        // ignore cancel errors
      }
    }
    setOidcLinkToken(null);
    setOidcProfile(null);
    setOidcPassword('');
  };

  if (user && token) return <Redirect to="/taps" />;

  return (
    <Grid container direction="row" className={classes.root}>
      <Grid item className={classes.header} xs={12} md={8}>
        <img src={Logo} alt="POURWALL" className={classes.logo} />
        <Hidden mdUp>
          <Typography variant="h1">{t('Login.WelcomeBack')}</Typography>
          <p>{t('Login.SignInToGetStarted')}</p>
        </Hidden>
        <Hidden smDown>
          <div className={classes.product}>
            <div className={classes.productText}>
              <img alt="" src={Glassware} className={classes.glassware} />
              <h1 className={classes.productHeading}>{t('Login.SimpleDigitalBeverageMenus')}</h1>
              <p className={classes.productDescription}>{t('Login.CreatedForBars')}</p>
            </div>
            <img alt="" src={Illustration} className={classes.barScene} />
          </div>
        </Hidden>
      </Grid>
      <Grid item className={classes.content} xs={12} md={4}>
        <Hidden smDown>
          <Typography variant="h1">{oidcLinkToken ? 'Confirm your password' : t('Login.WelcomeBack')}</Typography>
          <p>{oidcLinkToken ? 'This is a one-time security step to link your Google login.' : t('Login.SignInToGetStarted')}</p>
        </Hidden>
        {!oidcLinkToken && (
          <>
            <form className={classes.form} onSubmit={go}>
              <TextField
                variant="outlined"
                fullWidth
                label={t('Login.Email')}
                className={classes.input}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                variant="outlined"
                fullWidth
                label={t('Login.Password')}
                type="password"
                className={classes.input}
                onChange={(e) => setPassword(e.target.value)}
                onInput={(e) => setPassword(e.target.value)}
              />
              <p style={{ margin: 0 }}>
                {t('Login.ForgotYourPassword')} <CustomLink href="/forgotpassword">{t('Login.ResetIt')}</CustomLink>.
              </p>
              <Button type="submit" className={classes.button} variant="contained" color="primary" disabled={loading}>
                {t('Login.LetsGo')} <ArrowForward />
              </Button>
            </form>
          </>
        )}
        {oidcLinkToken && (
          <form className={classes.form} onSubmit={handleOidcConfirm}>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              For security, please confirm your password once to link this Google login. After this, you can sign in with Google without a
              password.
            </Typography>
            {oidcProfile?.email && (
              <Typography variant="body2" style={{ marginTop: 4 }}>
                {oidcProfile.email}
              </Typography>
            )}
            <TextField
              variant="outlined"
              fullWidth
              label={t('Login.Password')}
              type="password"
              className={classes.input}
              onChange={(e) => setOidcPassword(e.target.value)}
              value={oidcPassword}
            />
            <Button type="submit" className={classes.button} variant="contained" color="primary" disabled={oidcConfirmLoading}>
              Confirm and link
            </Button>
            <Button type="button" className={`${classes.button} ${classes.buttonOutline}`} variant="outlined" onClick={handleOidcCancel}>
              Cancel
            </Button>
          </form>
        )}
        {!oidcLinkToken && (
          <div>
            <p className="mb-4">
              {t('Login.DontHaveAnAccountYet')} <CustomLink href="/signup">{t('Login.SignUp')}</CustomLink>.
            </p>
            <div className={classes.googleSection}>
              <div className={classes.divider}>
                <div className={classes.dividerLine} />
                <span className={classes.dividerText}>OR</span>
                <div className={classes.dividerLine} />
              </div>
              <Button
                type="button"
                className={classes.googleButton}
                variant="outlined"
                onClick={handleOidcStart}
                disabled={oidcStartLoading || oidcLoginLoading}>
                <img className={classes.googleLogo} src={GoogleLogo} alt="Google" />
                Login with Google
              </Button>
            </div>
            <LanguageDropdown />
          </div>
        )}
      </Grid>
      <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
        <Alert variant="filled" onClose={handleClose} severity="error">
          {errorMessage || t('Login.PleaseCheckEmailOrPassword')}
        </Alert>
      </Snackbar>
    </Grid>
  );
};
